/**
 * The pick itself: search the pricebook and attribute a line to the item
 * chosen, or to none. Shared by the line list's "Change" and the review
 * queue's "Wrong item?", each of which owns the dialog and the mutation.
 */
import { CheckIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { CheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { DialogFooter } from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  useMatchLinePricebookItem,
  useSearchPricebookItems,
} from "@/accounts-payable/query";
import type {
  ApDocumentType,
  ApInvoiceItem,
  ApPricebookItemOption,
} from "@/accounts-payable/types";
import {
  documentTypeLabel,
  formatDollars,
  hasPricebookMatch,
  matchedPricebookItemLabel,
  pricebookItemLabel,
  pricebookOptionMeta,
  pricebookSearchStatusMessage,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useDebounce } from "@/core/hooks";
import { cn } from "@/core/utils/utils";
import { TextButton } from "./shared";

/** The search reads our own pricebook copy, so it can afford to be quick. */
const SEARCH_DEBOUNCE_MS = 250;

export const PricebookMatchForm = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  item,
  matchLine,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  agentRunId: string | null;
  item: ApInvoiceItem;
  matchLine: ReturnType<typeof useMatchLinePricebookItem>;
  onClose: () => void;
}) => {
  // The server can give the pick to the other lines sharing this one's SKU
  // (usually the same part) and says how many it would reach. Some vendors put
  // a product family in the SKU and the size in the description, so the
  // person picking can keep it to this line.
  const sameSkuLineCount = item.same_sku_line_count ?? 1;
  const [applyToSameSkuLines, setApplyToSameSkuLines] = useState(true);
  const lineCount = applyToSameSkuLines ? sameSkuLineCount : 1;
  // Seeded with the SKU: it is what the automatic match searched by first.
  const [query, setQuery] = useState(item.sku ?? "");
  const [selected, setSelected] = useState<ApPricebookItemOption | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const {
    data: options = [],
    isLoading,
    isError,
    isPlaceholderData,
  } = useSearchPricebookItems(businessId, debouncedQuery);

  // A selection belongs to the results it was picked from (see the job match).
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query.trim()) setSelected(null);
  };

  const isSearchPending =
    isLoading || isPlaceholderData || query !== debouncedQuery;
  const statusMessage = pricebookSearchStatusMessage({
    query,
    isLoading: isSearchPending,
    isError,
    resultCount: options.length,
  });

  const save = (pricebookItem: ApPricebookItemOption | null) =>
    matchLine.mutate(
      {
        documentId,
        lineItemId: item.id,
        pricebookItemId: pricebookItem?.id ?? null,
        ...(sameSkuLineCount > 1 && { applyToSameSkuLines }),
      },
      {
        onSuccess: (document) => {
          const lines = lineCount > 1 ? `${lineCount} lines` : "Line";
          const outcome = pricebookItem
            ? `${lines} matched to ${pricebookItemLabel(pricebookItem)}.`
            : `${lines} marked as not in the pricebook.`;
          // The server rejects the agent's open proposals and queues a run
          // that reads the pick, after a short wait so several edits share it.
          const rerunQueued =
            document.agent_run_id !== null &&
            document.agent_run_id !== agentRunId;
          toast.success(
            rerunQueued
              ? `${outcome} The agent will re-check this ${documentTypeLabel(documentType).lower} against it in a couple of minutes.`
              : outcome,
          );
          onClose();
        },
        onError: (error: unknown) =>
          toast.error("Couldn’t change the pricebook item", {
            description: getErrorMessage(error),
          }),
      },
    );

  return (
    <>
      <Stack gap="md" fullWidth className="px-6">
        <Stack gap="none" fullWidth>
          <Typography variant="bodyW2">
            {item.quantity} × {item.description}
          </Typography>
          <Typography variant="captionW1" textColor="tertiary">
            {[item.sku, formatDollars(item.unit_cost)]
              .filter(Boolean)
              .join(" · ")}
            {hasPricebookMatch(item) &&
              ` · now ${matchedPricebookItemLabel(item)}`}
          </Typography>
        </Stack>
        {sameSkuLineCount > 1 && (
          <CheckboxWithLabel
            id={`same-sku-lines-${item.id}`}
            label={`Also apply to the ${sameSkuLineCount - 1} other ${sameSkuLineCount === 2 ? "line" : "lines"} invoiced as ${item.sku}`}
            checked={applyToSameSkuLines}
            disabled={matchLine.isPending}
            onCheckedChange={(checked) =>
              setApplyToSameSkuLines(checked === true)
            }
          />
        )}
        <Command
          shouldFilter={false}
          label="Search the pricebook"
          className="overflow-visible rounded-lg border border-stroke-tertiary"
        >
          <CommandInput
            value={query}
            onValueChange={onQueryChange}
            disabled={matchLine.isPending}
            placeholder="Search by code, name, or description"
          />
          <CommandList className="max-h-64 overflow-y-auto">
            {statusMessage ? (
              <Stack
                align="center"
                fullWidth
                role="status"
                className="px-4 py-6 text-center text-md text-tertiary-text"
              >
                {statusMessage}
              </Stack>
            ) : (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected?.id === option.id;
                  const isCurrent =
                    option.external_id === item.matched_pricebook_item_id &&
                    option.item_type === item.matched_pricebook_item_type;
                  return (
                    <CommandItem
                      key={option.id}
                      value={option.id}
                      disabled={matchLine.isPending}
                      onSelect={() => setSelected(option)}
                      className={cn("items-start gap-3 px-3 py-2", {
                        "bg-bg-secondary": isSelected,
                      })}
                    >
                      <CheckIcon
                        className={cn("mt-1 h-4 w-4 flex-none", {
                          "opacity-0": !isSelected,
                        })}
                      />
                      <Stack gap="none" className="min-w-0 flex-1">
                        <Stack
                          direction="row"
                          gap="sm"
                          align="baseline"
                          className="flex-wrap"
                        >
                          <span className="font-semibold">
                            {option.name ?? option.code ?? "Unnamed item"}
                          </span>
                          {isCurrent && (
                            <span className="text-sm text-tertiary-text">
                              Current match
                            </span>
                          )}
                        </Stack>
                        <span className="text-sm text-quaternary-text">
                          {pricebookOptionMeta(option).join(" · ")}
                        </span>
                      </Stack>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </Stack>
      {/* One row at every width: the default footer stacks in reverse on
          phones, which pushed the clear action under the buttons. */}
      <DialogFooter className="flex-row flex-wrap items-center justify-between gap-3 sm:justify-between sm:space-x-0">
        {/* Clearing is for a line that isn't stocked: it keeps it unmatched
            and stops any pending cost review raised against the wrong item. */}
        {hasPricebookMatch(item) ? (
          <TextButton
            tone="secondary"
            onClick={() => save(null)}
            disabled={matchLine.isPending}
            className="whitespace-nowrap"
          >
            Not in the pricebook
          </TextButton>
        ) : (
          <Stack />
        )}
        <Stack direction="row" gap="sm">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={matchLine.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => selected && save(selected)}
            loading={matchLine.isPending}
            disabled={!selected || matchLine.isPending}
          >
            {/* Not the item's name: pricebook names run long, and the pick is
                already checked in the list above. */}
            Match to item
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
};
